import { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { asyncLogout } from '../../redux/slices/auth';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  CHAT_PERMISSION,
  MOOD_TYPE,
  ROOT_PATHS,
  SUCCESS_STATUS
} from '../../Constants';
import { getLocales, setLocaleIsOpenUnsavedModal } from '../../redux/slices/locales';
import { ApiIssueCategory, ApiIssuePriorityLevel, ApiLocaleDef, ApiSubmitIssueRequest } from '@agunity/api-v4';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { AgColor } from '../../stories/Styles/AgColor';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import Locale from './Locale';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AgOrgNavigation from '../../stories/Components/Navigations/AgOrgNavigation';
import { RenderTree, updateNodeInTree } from '../../stories/Utils/TreeUtils';
import {
  getOrgDirectChildren,
  getStructure,
  setSearchOrgId
} from '../../redux/slices/organizations';
import { selectFullProfile } from '../../redux/slices/fullProfile';
import {
  setFunctionStructureList,
  setIsOpenSupportModal,
  setIsOpenUnsavedModal,
  setMember_Redirect_URL
} from '../../redux/slices/member';
import AgTopNavigation from '../../stories/Components/Navigations/AgTopNavigation';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AgModal from '../../stories/AgModal';
import AgTypography from '../../stories/Styles/AgTypography';
import AgButton from '../../stories/Components/AgButton';
import AgMoodSelector from '../../stories/AgMood/AgMoodSelector';
import AgTextArea from '../../stories/Components/AgTextArea';
import AgToast from '../../stories/Components/AgToast';
import {
  closeChatModal,
  closeFeedbackModal,
  postChatModalMsg,
  startChatModal,
  submitFeedback,
  submitIssue
} from '../../redux/slices/feedback';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import AgDropdown from '../../stories/AgDropdown';
import { selectOrgTree, setOrgTree } from '../../redux/slices/global';
import { BreadCrumbsProps } from '../../stories/Components/AgBreadCrumbs';
import { cloneDeep } from 'lodash';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import AgIconButton from '../../stories/AgIconButton';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import BreadcrumbsBarWithBackButton from '../BreadcrumbsBar/BreadcrumbsBarWithBackButton';
import { setIsOpenCertificateUnsavedModal } from '../../redux/slices/certificate';
import BusinessIcon from '@mui/icons-material/Business';
import AgTextField from '../../stories/Components/AgTextField';
import { useToast } from '../../redux/hooks/toast';

interface NavbarProps {
  setOpen: Function;
  open?: boolean;
  setOpenSidebar: Function;
  opensidebar?: boolean;
}

interface ExtendedRenderTree extends RenderTree {
  hasCh?: boolean;
}

export default function Navbar(props: NavbarProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const messages = useIntl().formatMessage;
  const [locale, setLocale] = useState<ApiLocaleDef[]>();
  const { toast } = useToast();

  const profile = useAppSelector(selectFullProfile);
  const orgTree = useAppSelector(selectOrgTree);
  const memberFullName = useAppSelector(state => state.member.editMemberFullName);
  const staffFullName = useAppSelector(state => state.staff.editStaffFullName);
  const showOrgNavState = useAppSelector(state => state.navigation.showOrgNav);
  const headerName = useAppSelector(state => state.navigation.headerName);
  const profileOrgName =
    profile?.organizations?.length &&
    profile?.organizations?.length > 0 &&
    profile?.organizations[0]?.name!;
  const profileOrgId =
    profile?.organizations?.length &&
    profile?.organizations?.length > 0 &&
    profile?.organizations[0]?.id!;
  const orgName = useAppSelector(state => state.orgs.selectedOrgName);
  const [functionListWhoHasChildOrgs, setFunctionListWhoHasChildOrgs] =
    useState<number[]>([]);
  const user = useAppSelector((state) => state.global.users);
  const permissions = (user?.length && user[0].permissions) || [];
  const hasChatPer = permissions.includes(CHAT_PERMISSION);
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
  const [showChatModal, setShowChatModal] = useState<boolean>(false);
  const [showFeedbackLaterModal, setShowFeedbackLaterModal] =
    useState<boolean>(false);
  const [mood, setMood] = useState<MOOD_TYPE>();
  const [moodState, setMoodState] = useState<{
    mood: MOOD_TYPE;
    q1: string;
    q2: string;
  }>({ mood: mood ?? MOOD_TYPE.VerySatisfied, q1: '', q2: '' });
  const [msg, setMsg] = useState<boolean>(false);
  const [msgSupport, setMsgSupport] = useState<boolean>(false);

  const location = useLocation();
  const showOrgNav = [ROOT_PATHS.MEMBERS, ROOT_PATHS.BULK_UPLOAD_TEMPLATES, ROOT_PATHS.STAFF, ROOT_PATHS.ORGANISATIONS].includes(location.pathname) || showOrgNavState;
  const isDirty_Member_Personal = useAppSelector(
    (state) => state.member.isDirty_Member_Personal
  );
  const locale_newLocaleFileUploaded = useAppSelector(state => state.language.locale_newLocaleFileUploaded);
  const helpLink = useAppSelector((state) => (state.language.helpLink));
  const breadcrumbsText = useAppSelector((state) => state.certificate.certificateBreadcrumbs);
  const showOrgNavOnOtherPages =
    location.pathname === ROOT_PATHS.HELP ||
    location.pathname === ROOT_PATHS.ADD_MEMBER ||
    location.pathname.startsWith(ROOT_PATHS.MEMBER) ||
    location.pathname === ROOT_PATHS.ADD_STAFF ||
    /^\/certificate\/\d+$/.test(location.pathname) ||
    location.pathname.startsWith(ROOT_PATHS.ADD_CERTIFICATE) ||
    location.pathname.startsWith(ROOT_PATHS.ORGANISATION) ||
    location.pathname === ROOT_PATHS.ADD_ORGANISATION ||
    new RegExp(`^(?:${ROOT_PATHS.LICENSES}/\\d+|${ROOT_PATHS.ADD_LICENSE})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.CUSTOM_FIELDS}/\\d+|${ROOT_PATHS.ADD_CUSTOM_FIELDS})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.ENUMS}/\\d+|${ROOT_PATHS.ADD_ENUM})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.ASSET_TYPE_LIST}/\\d+|${ROOT_PATHS.ADD_ASSET_TYPE_LIST})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.UNITS}/\\d+|${ROOT_PATHS.ADD_UNIT})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.CONTRACT_TYPES}/\\d+|${ROOT_PATHS.ADD_CONTRACT_TYPE})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.TRAINING_TOPICS}/\\d+|${ROOT_PATHS.ADD_TRAINING_TOPIC})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.TRAININGS}/\\d+|${ROOT_PATHS.ADD_TRAINING})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.BULK_UPLOAD_REQUEST}/[\\w-]+)$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.BULK_PURCHASE}/[\\w-]+)$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.BULK_UPLOAD}/\\d+)$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.BATCH}/\\d+|${ROOT_PATHS.ADD_BATCH})$`).test(location.pathname) ||
    new RegExp(`^(?:${ROOT_PATHS.CERTIFICATE_TYPES}/\\d+|${ROOT_PATHS.ADD_CERTIFICATE_TYPE})$`).test(location.pathname);

  location.pathname.startsWith(ROOT_PATHS.ACCOUNT) ||
    (new RegExp(`^${ROOT_PATHS.BULK_UPLOAD}/\\d+`, 'g').test(location.pathname));
  const getHeaderName = (location: string) => {
    if (location === ROOT_PATHS.HELP) {
      return messages({ id: 'HELP_DOC' });
    } else if (new RegExp(`^${ROOT_PATHS.BULK_UPLOAD}/\\d+`, 'g').test(location)) {
      return messages({ id: 'BULK_UPLOAD' });
    } else if (location === ROOT_PATHS.ADD_MEMBER) {
      return messages({ id: 'MEMBER_ADD_NEW_MEMBER' });
    } else if (location.startsWith(ROOT_PATHS.MEMBER)) {
      return memberFullName;
    } else if (location === ROOT_PATHS.ADD_STAFF) {
      return messages({ id: 'STAFF_ADD_A_STAFF' });
    } else if (location.startsWith(ROOT_PATHS.STAFF)) {
      return staffFullName;
    } else if (location.startsWith(ROOT_PATHS.ORGANISATION)) {
      return orgName || '';
    } else if (location === ROOT_PATHS.ADD_ORGANISATION) {
      return messages({ id: 'NEW_ORGANIZATION' });
    } else if (location.startsWith(ROOT_PATHS.ACCOUNT)) {
      return messages({ id: 'MY_ACCOUNT' });
    } else {
      return headerName ?? '';
    }
  };
  const orgNavOnOtherPages_HeaderName = getHeaderName(location.pathname);
  const hasCertificateChanged = useAppSelector(state => state.certificate.hasCertificateChanged);

  const initialSupportState: ApiSubmitIssueRequest = {
    title: '',
    description: '',
    level: undefined,
    category: undefined,
  };

  const issueLevels = useMemo(() => {
    return Object.values(ApiIssuePriorityLevel)
    .filter((level) => Number(level) >= 0)
    .map((level) => ({ id: Number(level), name: messages({ id: `ISSUE_PRIORITY_LEVEL_${level}` }) }));
  }, [messages]);

  const categories = useMemo(() => {
    return Object.values(ApiIssueCategory)
    .filter((category) => Number(category) >= 0)
    .map((category) => ({ id: Number(category), name: messages({ id: `ISSUE_CATEGORY_${category}` }) }));
  }, [messages]);

  const currentPath = window.location.pathname;
  const pathRegex = /(\/(member\/|add_member)(\/(\d+))?)/;
  const lastFeedbackAsOf = profile?.account?.lastFeedbackAsOf;
  const [showSupport, setShowSupport] = useState<boolean>(false);
  const [supportState, setSupportState] = useState<ApiSubmitIssueRequest>(initialSupportState);
  const isOpenSupportModal = useAppSelector(
    (state) => state.member.isOpenSupportModal
  );
  const isOnFreemium =
    profile?.organizations &&
    profile?.organizations[0]?.license?.supportLevel === 0;
  const [chatMessages, setChatMessages] = useState<
    { source: 'server' | 'client'; text: string }[]
  >([]);
  const [currMsg, setCurrMsg] = useState<string>('');
  const [disabledChat, setDisabledChat] = useState<boolean>(false);
  const [navLoadingId, setNavLoadingId] = useState<number>(0);

  const getNodeChildren = async (nodeId: string | null, setSearch: boolean) => {
    if (!nodeId) return;
    setNavLoadingId(Number(nodeId));

    try {
      const structure = await dispatch(getStructure());
      const childOrgs = await dispatch(getOrgDirectChildren(Number(nodeId)));
      if (childOrgs.status !== SUCCESS_STATUS) return;

      const orgsHasChild = childOrgs.data.filter((item) => functionListWhoHasChildOrgs.includes(item.functionId!));
      const updatedTree = updateNodeInTree(orgTree as RenderTree, nodeId!, {
        children: orgsHasChild.map((org) => {
          const hasChild = !structure?.data
            .find((st) => st.id === org.functionId)
            ?.items?.map((or) => structure.data.find((s) => s.id === or)?.supportIndirect)
            ?.find((t) => t);

          return {
            id: org.id?.toString() || '',
            name: org.name || '',
            children: [],
            hasCh: hasChild
          };
        })
      });
      
      dispatch(setOrgTree(updatedTree));
      if (setSearch) dispatch(setSearchOrgId(nodeId!));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setNavLoadingId(0);
    }
  };

  const addBreadCrumbToCurrentChildren = (
    data: ExtendedRenderTree,
    breadcrumbs: BreadCrumbsProps[]
  ) => {
    // add breadCrumb to orgTree (in the case redirect from external link)
    let pathLength = breadcrumbs.length;
    let updatedOrgData = cloneDeep(data);
    const originalOrgData = updatedOrgData;
    if (breadcrumbs[0].name !== data.name) return;
    for (let i = 1; i < pathLength; i++) {
      if (breadcrumbs[i].id === 'select') return;
      // add all treePath into currentChildren
      updatedOrgData.children = updatedOrgData.children?.filter(
        (item) => item.name && item.id
      );
      const isChildrenExist = updatedOrgData.children?.find(
        (child) => child.name === breadcrumbs[i].name
      );
      if (isChildrenExist) {
        updatedOrgData = isChildrenExist;
      } else {
        const children = updatedOrgData.children || [];
        updatedOrgData = {
          id: breadcrumbs[i].id?.toString() ?? '',
          name: breadcrumbs[i].name ?? '',
          children: []
        };
        children.push(updatedOrgData);
      }
    }
    return originalOrgData;
  };

  const handleClick = (url: string) => {
    if (isDirty_Member_Personal && currentPath.match(pathRegex)){
      dispatch(setMember_Redirect_URL(url));
      dispatch(setIsOpenUnsavedModal(true));
    } else if (currentPath === ROOT_PATHS.LOCALISATION && locale_newLocaleFileUploaded) {
      dispatch(setMember_Redirect_URL(url));
      dispatch(setLocaleIsOpenUnsavedModal(true));
    } else if ((currentPath.startsWith(ROOT_PATHS.CERTIFICATE) || currentPath === ROOT_PATHS.ADD_CERTIFICATE) && hasCertificateChanged) {
      dispatch(setMember_Redirect_URL(url));
      dispatch(setIsOpenCertificateUnsavedModal(true));
    } else{
      navigate(url, { replace: true });
    }
  };

  useEffect(() => {
    if (!mood) return;
    setMoodState({ ...moodState, mood: mood });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mood]);

  useEffect(() => {
    if (profile?.shouldShowFeedback) {
      setShowFeedbackModal(profile?.shouldShowFeedback);
    }
  }, [profile?.shouldShowFeedback]);

  useEffect(() => {
    try {
      const f = async () => {
        const structure = await dispatch(getStructure());
        if (structure?.data.length) {
          dispatch(setFunctionStructureList(structure.data));
        }
        const functionIds = structure?.data
          .filter((item) => !item.supportIndirect)
          .map((item) => {
            return item.id!;
          });
        setFunctionListWhoHasChildOrgs(functionIds || []);

        if (!profileOrgId) return;
        const childOrgs = await dispatch(getOrgDirectChildren(profileOrgId));
        if (childOrgs.status !== SUCCESS_STATUS) return;
        const orgsHasChild = childOrgs.data.filter((item) =>
          functionIds?.includes(item.functionId!)
        );

        const OrgChildrenTree = orgsHasChild.map((org) => {
          const hasChil = !structure?.data
            .find((st) => st.id === org.functionId)
            ?.items?.map(
              (or) => structure.data.find((s) => s.id === or)?.supportIndirect
            )
            ?.find((t) => t);
          return {
            id: org.id?.toString() || '',
            name: org.name!,
            children: [
              // {
              //   id: '',
              //   name: ''
              // }
            ],
            hasCh: hasChil
          };
        });
        let tempOrgTree: ExtendedRenderTree = {
          id: profileOrgId.toString(),
          name: profileOrgName?.toString() || 'N/A',
          children: OrgChildrenTree
        };
        const currentBreadcrumb: BreadCrumbsProps[] = JSON.parse(
          localStorage.getItem('orgBreadcrumb') || '[]'
        );
        if (currentBreadcrumb.length)
          tempOrgTree =
            addBreadCrumbToCurrentChildren(tempOrgTree, currentBreadcrumb) ??
            tempOrgTree; // if redirect from external link, then add breadcrumb to orgTree
        dispatch(setOrgTree(tempOrgTree));
        dispatch(setSearchOrgId(profileOrgId.toString()));
      };
      f();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }, [dispatch, profileOrgId, profileOrgName]);

  useEffect(() => {
    const f = async () => {
      const loc = await dispatch(getLocales());
      loc?.data && setLocale(loc.data);
    };

    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const helpItem = isOnFreemium
    ? {
      icon: (
        <HelpOutlineIcon
          style={{
            width: '15px',
            height: '15px'
          }}
        />
      ),
      name: messages({ id: 'HELP' }),
      onClick: () => { window.open(helpLink, '_blank'); }
    }
    : {};

  const chatItem = hasChatPer
    ? {
      icon: (
        <SmsOutlinedIcon
          style={{
            width: '15px',
            height: '15px'
          }}
        />
      ),
      name: messages({ id: 'CHAT' }),
      onClick: async () => {
        setShowChatModal(!showChatModal);
        const res = await dispatch(startChatModal());
        if (res.status === SUCCESS_STATUS && res.data.text)
          setChatMessages([{ source: 'server', text: res.data.text }]);
      }
    }
    : {};

  const navItems = [
    {
      icon: !props.opensidebar ? (
        <MenuIcon style={{ float: 'left', cursor: 'pointer' }} />
      ) : (
        <CloseIcon style={{ float: 'left', cursor: 'pointer' }} />
      ),
      name: 'menu',
      onClick: () => {
        props.setOpenSidebar(!props.opensidebar);
      }
    },
    helpItem,
    chatItem,
    {
      icon: (
        <RateReviewOutlinedIcon
          style={{
            width: '15px',
            height: '15px'
          }}
        />
      ),
      name: messages({ id: 'FEEDBACK' }),
      onClick: () => setShowFeedbackModal(!showFeedbackModal)
    }
  ];
  const profileMenuItems = [
    {
      icon: <AccountCircleOutlinedIcon />,
      name: messages({ id: 'MY_ACCOUNT' }),
      onClick: () => {handleClick(ROOT_PATHS.ACCOUNT + ROOT_PATHS.PROFILE);},
    },
    {
      component: <Locale expandToLeft locale={locale || []} />
    },
    {
      icon: <BusinessIcon />,
      name: messages({ id: 'MY_ORGANIZATION' }),
      onClick: () => {handleClick(ROOT_PATHS.ACCOUNT + ROOT_PATHS.MY_ORGANISATION);},
    },
    {
      icon: <LogoutIcon />,
      name: messages({ id: 'LOGOUT' }),
      onClick: async () => {
        await dispatch(asyncLogout());
      },
      style: {
        borderTop: `1px solid ${AgColor.grey3}`
      }
    }
  ];

  const submitFeedbackData = async () => {
    try {
      await dispatch(
        submitFeedback({
          satisfactionLevel:
            mood === MOOD_TYPE.VerySatisfied
              ? 0
              : mood === MOOD_TYPE.Satisfied
                ? 1
                : mood === MOOD_TYPE.Dissatisfied
                  ? 2
                  : 3,
          generalComment: moodState.q1,
          improvement: moodState.q2
        })
      );
      setMsg(true);
      setShowFeedbackModal(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const handleSubmitIssue = async () => {
    try {
      // eslint-disable-next-line eqeqeq
      if (Object.values(supportState).some((v) => v === '' || v == null)) {
        toast('error', messages({ id: 'ISSUE_INVALID' }), { temporary: true });
        return;
      }

      await dispatch(submitIssue(supportState));
      setMsgSupport(true);
      setShowSupport(false);
      dispatch(setIsOpenSupportModal(false));
      setSupportState(initialSupportState);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const helpMenuItems = isOnFreemium
    ? undefined
    : [
      {
        icon: <QuizOutlinedIcon />,
        name: messages({ id: 'HELP_DOC' }),
        onClick: () => { window.open(helpLink, '_blank'); },
      },
      {
        icon: <LiveHelpOutlinedIcon />,
        name: messages({ id: 'SUPPORT' }),
        onClick: () => {
          setShowSupport(true);
        }
      }
    ];

  const submitSend = async () => {
    try {
      setDisabledChat(true);
      const arr = chatMessages;
      arr.push({ source: 'client', text: currMsg });
      setChatMessages(arr);
      const res = await dispatch(postChatModalMsg({ text: currMsg }));
      if (res.status === SUCCESS_STATUS && res.data.text) {
        const arr2 = chatMessages;
        arr2.push({ source: 'server', text: res.data.text });
        setChatMessages(arr2);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setCurrMsg('');
      setDisabledChat(false);
    }
  };
  const renderTextWithClickableURLs = (text: string = '', isServer: boolean) => {
    // 5356: The API response will only provide strings and HTML.
    return <div style={{ width: 'fit-content', marginLeft: isServer ? 'auto' : 'unset' }} dangerouslySetInnerHTML={{ __html: text }} />;
  };

  return (
    <Box sx={{ backgroundColor: AgColor.background }}>
      <Box>
        <AgTopNavigation
          profileMenuItems={profileMenuItems}
          navItems={navItems}
          showIcon={false}
          helpMenuItems={helpMenuItems}
          breadCrumb={
            showOrgNav
              ? (
                <AgOrgNavigation
                  data={orgTree as ExtendedRenderTree}
                  getNodeChildren={getNodeChildren}
                  loadingId={navLoadingId}
                />
              )
              : showOrgNavOnOtherPages
                ? <AgTypography
                  style={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <BreadcrumbsBarWithBackButton headerName={orgNavOnOtherPages_HeaderName} isDirty={isDirty_Member_Personal}/>
                  <AgTypography contentStyle={'AP.H5.Medium'} message={breadcrumbsText} style={{ color: AgColor.secondaryDark, marginLeft: '-32px' }} />
                </AgTypography>
                : undefined
          }
        />
      </Box>

      {/* chat modal */}
      <AgModal
        key='chat-modal'
        customWidth='50%'
        open={showChatModal}
        setOpen={setShowChatModal}
        closeIcon
        onCloseModal={async () => {
          await dispatch(closeChatModal());
          setChatMessages([]);
          setCurrMsg('');
        }}
        body={
          <Box sx={{ textAlign: 'left', minHeight: '580px',  }}>
            <Box
              sx={{
                height: '470px',
                marginTop: '-16px',
                border: `1px solid ${AgColor.grey4}`,
                bgcolor: AgColor.grey2,
                borderRadius: '5px',
                padding: '16px',
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column-reverse'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {chatMessages.map((item, index) => {
                  const isServer = item.source === 'server';
                  return (
                    <AgTypography
                      key={index}
                      sx={{
                        overflow: 'auto',
                        bgcolor: AgColor.grey0,
                        marginBottom: '8px',
                        padding: '8px',
                        borderRadius: '5px',
                        minWidth: '30%',
                        maxWidth: '80%',
                        width: 'max-content',
                        marginLeft: isServer ? 'auto' : 'unset',
                        textAlign: isServer ? 'right' : 'left'
                      }}
                    >
                      <AgTypography contentStyle='AP.H4.Bold'>
                        {item.source === 'client'
                          ? profile?.account?.firstName.toUpperCase() ||
                            item.source.toUpperCase()
                          : item.source.toUpperCase()}
                      </AgTypography>
                      {renderTextWithClickableURLs(item.text, isServer)}
                    </AgTypography>
                  );
                })}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ width: '100%' }} key={`textarea-${disabledChat}`}>
                <AgTextArea
                  autofocus
                  onChange={(e) => setCurrMsg(e.target.value)}
                  placeholder={messages({ id: 'WRITE_SOMETHING' })}
                  required
                  noStar
                  style={{ width: 'calc(100% - 80px)' }}
                  value={currMsg}
                  disabled={disabledChat}
                  enterKeyPressed={submitSend}
                />
              </div>

              <AgIconButton
                icon={<SendOutlinedIcon />}
                onClick={submitSend}
                style={{ height: '8px' }}
              />
            </Box>
          </Box>
        }
      />

      {/* feedback modal */}
      <AgModal
        key='feedback-modal'
        width={640}
        open={showFeedbackModal}
        setOpen={setShowFeedbackModal}
        closeIcon
        onCloseModal={async () => {
          await dispatch(closeFeedbackModal());
        }}
        state={messages({ id: 'FEEDBACK' })}
        stateColor={AgColor.grey6}
        title={messages({ id: 'FEEDBACK_MSG1' })}
        body={
          <Box sx={{ marginBottom: '90px', textAlign: 'left' }}>
            <AgTypography
              contentStyle='AP.Paragraph.Large.SemiBold'
              sx={{ margin: '16px 0px' }}
            >
              {messages({ id: 'FEEDBACK_MSG2' })}
            </AgTypography>
            <AgMoodSelector setMood={setMood} />
            <AgTypography
              contentStyle='AP.Paragraph.Large.SemiBold'
              sx={{ marginTop: '16px' }}
            >
              {messages({ id: 'FEEDBACK_MSG3' })}
            </AgTypography>
            <AgTextArea
              onChange={(e) =>
                setMoodState({ ...moodState, q1: e.target.value })
              }
              maxLength={1200}
              placeholder={messages({ id: 'FEEDBACK_MSG4' })}
              required
              noStar
            />
            <AgTypography
              contentStyle='AP.Paragraph.Large.SemiBold'
              sx={{ marginTop: '16px' }}
            >
              {messages({ id: 'FEEDBACK_MSG5' })}
            </AgTypography>
            <AgTextArea
              onChange={(e) =>
                setMoodState({ ...moodState, q2: e.target.value })
              }
              maxLength={1200}
              placeholder={messages({ id: 'FEEDBACK_MSG4' })}
              required
              noStar
            />
          </Box>
        }
        footer={
          <>
            {lastFeedbackAsOf && profile?.shouldShowFeedback && (
              <AgButton
                style={{ marginRight: '32px' }}
                label={messages({ id: 'FEEDBACK_ASK_AGAIN' })}
                onClick={() => {
                  setShowFeedbackLaterModal(true);
                  setShowFeedbackModal(false);
                }}
                variant='outlined'
              />
            )}
            <AgButton
              disabled={!mood || moodState.q1 === '' || moodState.q2 === ''}
              label={messages({ id: 'FINISH' })}
              onClick={submitFeedbackData}
            />
          </>
        }
      />
      {/* ask later modal */}
      <AgModal
        key='later-modal'
        width={640}
        open={showFeedbackLaterModal}
        setOpen={setShowFeedbackLaterModal}
        closeIcon
        title={messages({ id: 'FEEDBACK_MSG7' })}
        body={
          <Box sx={{ marginBottom: '90px', textAlign: 'left' }}>
            <AgTypography
              contentStyle='AP.Paragraph.Large.Regular'
              sx={{ margin: '16px 0px' }}
            >
              {messages({ id: 'FEEDBACK_MSG8' })}
            </AgTypography>
            <AgTypography
              contentStyle='AP.Paragraph.Large.Regular'
              sx={{ marginTop: '16px' }}
            >
              {messages({ id: 'FEEDBACK_MSG9' })}
            </AgTypography>
          </Box>
        }
        footer={
          <>
            <AgButton
              label={messages({ id: 'ACCESS_THE_HUB' })}
              onClick={() => setShowFeedbackLaterModal(false)}
            />
          </>
        }
      />
      {/* support modal */}
      <AgModal
        bodySX={{ overflowX: 'hidden' }}
        key='support-modal'
        open={showSupport || isOpenSupportModal}
        setOpen={setShowSupport}
        maxHeight={800}
        closeIcon
        onCloseModal={() => dispatch(setIsOpenSupportModal(false))}
        state={messages({ id: 'SUPPORT' })}
        stateColor={AgColor.grey6}
        title={messages({ id: 'SUBMIT_ISSUE' })}
        body={
          <Box sx={{ marginBottom: '90px', textAlign: 'left' }}>
            <AgTypography
              contentStyle='AP.Paragraph.Large.Regular'
              sx={{ margin: '16px 0px' }}
            >
              {messages({ id: 'SUPPORT_MSG' })}
            </AgTypography>
            <AgTypography
              contentStyle='AP.H4.Medium'
              sx={{ marginTop: '32px', marginBottom: '-8px' }}
            >
              {messages({ id: 'TITLE2' })} *
            </AgTypography>
            <AgTextField
              onChange={(e) => setSupportState({ ...supportState, title: e.target.value })}
              placeholder=''              
              required
            />
            <AgTypography
              contentStyle='AP.H4.Medium'
              sx={{ marginTop: '24px' }}
            >
              {messages({ id: 'CATEGORY' })} *
            </AgTypography>
            <AgDropdown
              value={
                categories.find((item) => item.id === supportState?.category)?.name
              }
              onChange={(e: string) =>
                e &&
                setSupportState({
                  ...supportState,
                  category: categories.find((item) => item.name === e)?.id
                })
              }
              width={640}
              placeholder={messages({ id: 'SELECT' })}
              options={Object.values(categories)}
            />
            <AgTypography
              contentStyle='AP.H4.Medium'
              sx={{ marginTop: '24px' }}
            >
              {messages({ id: 'URGENCY_LEVEL' })} *
            </AgTypography>
            <AgDropdown
              value={
                issueLevels.find((item) => item.id === supportState?.level)?.name
              }
              onChange={(e: string) =>
                e &&
                setSupportState({
                  ...supportState,
                  level: issueLevels.find((item) => item.name === e)?.id
                })
              }
              width={640}
              placeholder={messages({ id: 'SELECT' })}
              options={Object.values(issueLevels)}
            />
            <AgTypography
              contentStyle='AP.H4.Medium'
              sx={{
                marginTop: '24px',
                marginBottom: '-8px'
              }}
            >
              {messages({ id: 'DESCRIBE_THE_ISSUE' })} *
            </AgTypography>
            <AgTextArea
              onChange={(e) =>
                setSupportState({ ...supportState, description: e.target.value })
              }
              maxLength={1200}
              placeholder={messages({ id: 'FEEDBACK_MSG4' })}
              required
              noStar
            />
          </Box>
        }
        footer={
          <>
            <AgButton
              label={messages({ id: 'SUBMIT' })}
              onClick={handleSubmitIssue}
            />
          </>
        }
      />
      {msg && (
        <AgToast
          severity={'success'}
          key='msg'
          message={messages({ id: 'FEEDBACK_MSG6' })}
          open={msg}
          onIconClick={() => setMsg(false)}
        />
      )}
      {msgSupport && (
        <AgToast
          severity={'success'}
          key='msg-support'
          message={messages({ id: 'RECEIVED_YOUR_ISSUE' })}
          open={msgSupport}
          onIconClick={() => setMsgSupport(false)}
        />
      )}
    </Box>
  );
}
